import React, { useEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

import Button from "../atomic/Button";
import * as PDFJS from "pdfjs-dist/build/pdf";
import { DocumentIcon, XMarkIcon } from "@heroicons/react/24/outline";

interface PDFViewerProps {
  isOpen: boolean;
  name?: string;
  pdfUrl: string;
  onClose?: () => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  isOpen,
  name,
  pdfUrl,
  onClose
}) => {
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [pageNum, setPageNum] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [isError, setIsError] = useState<boolean>(false);
  const [scale, setScale] = useState<number>(1);
  const [fitScale, setFitScale] = useState<number>(1);
  const [initialScale, setInitialScale] = useState<number>(1);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleContextMenu = (e) => {
      e?.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  const computeFitScale = async (pdfInfo: any) => {
    if (!pdfInfo || !containerRef.current) return 1;

    const page = await pdfInfo.getPage(1);
    const unscaledViewport = page.getViewport({ scale: 1 });

    const containerWidth =
      containerRef.current.clientWidth || window.innerWidth;
    const maxHeight =
      (typeof window !== "undefined" ? window.innerHeight : 800) - 200; // leave some space for controls

    const widthScale = containerWidth / unscaledViewport.width;
    const heightScale = maxHeight / unscaledViewport.height;

    const nextFitScale = Math.min(widthScale, heightScale);
    setFitScale(nextFitScale);

    return nextFitScale;
  };

  const renderPage = async (
    num: number,
    pdfInfo: any,
    overrideScale?: number
  ) => {
    if (!pdfInfo || !canvasRef.current) return;

    const page = await pdfInfo.getPage(num);
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");

    if (!context) return;

    const effectiveScale = overrideScale || scale || 1;
    const viewport = page.getViewport({ scale: effectiveScale });
    canvas.height = viewport.height || 0;
    canvas.width = viewport.width || 0;

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
      enableWebGL: true,
      antialiasText: true
    };

    // Clear the canvas before rendering the new page
    context.clearRect(0, 0, canvas.width, canvas.height);

    await page.render(renderContext).promise;

    setPageNum(num);
  };

  useEffect(() => {
    // Initialize PDF.js
    PDFJS.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS.version}/pdf.worker.min.js`;

    const isMobile =
      typeof window !== "undefined" ? window.innerWidth < 768 : false;

    // Fetch the PDF file and render the first page
    PDFJS?.getDocument(pdfUrl)
      ?.promise?.then(async (loadedPdfDoc: any) => {
        setPdfDoc(loadedPdfDoc);
        setPageCount(loadedPdfDoc.numPages);

        if (isMobile) {
          // Mobile: start noticeably zoomed-in from fit so content is readable
          const baseFitScale = await computeFitScale(loadedPdfDoc);
          const mobileInitialScale = baseFitScale * 1.5;
          setFitScale(baseFitScale);
          setScale(mobileInitialScale);
          setInitialScale(mobileInitialScale);

          await renderPage(1, loadedPdfDoc, mobileInitialScale);
        } else {
          // Desktop: preserve existing behavior with fixed 1.5 scale
          const desktopScale = 1.5;
          setFitScale(desktopScale);
          setScale(desktopScale);
          setInitialScale(desktopScale);
          await renderPage(1, loadedPdfDoc, desktopScale);
        }
      })
      .catch((error: any) => {
        console.error("Error loading PDF:", error);
        setIsError(true);
      });
  }, [pdfUrl, isOpen]);

  const previousPage = () => {
    if (pageNum <= 1) return;
    renderPage(pageNum - 1, pdfDoc);
  };

  const nextPage = () => {
    if (pageNum >= pageCount) return;
    renderPage(pageNum + 1, pdfDoc);
  };

  const handleZoomIn = () => {
    if (!pdfDoc) return;
    const newScale = scale * 1.25;
    setScale(newScale);
    renderPage(pageNum, pdfDoc, newScale);
  };

  const handleZoomOut = () => {
    if (!pdfDoc) return;
    const newScale = Math.max(fitScale, scale / 1.25);
    setScale(newScale);
    renderPage(pageNum, pdfDoc, newScale);
  };

  const handleResetZoom = () => {
    if (!pdfDoc) return;
    setScale(initialScale);
    renderPage(pageNum, pdfDoc, initialScale);
  };

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col items-center justify-center"
    >
      {name && (
        <div className="mb-6 text-white font-semibold text-lg text-center">
          {name?.replace("_", " ")}
        </div>
      )}

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 text-white bg-black/60 rounded-full px-3 py-1 text-xs font-semibold hover:bg-black/80"
        >
          X
        </button>
      )}

      {isError ? (
        <>
          <div className="text-white mt-10">
            <div className="relative text-center flex justify-center">
              <DocumentIcon className="h-20 w-20 text-inherit" />
              <XMarkIcon className="h-10 w-10 text-white absolute top-7 " />
            </div>
            <div className="font-semibold text-lg mt-5">
              Something went wrong
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-center mb-4 space-x-2">
            <Button
              color="primary"
              onClick={handleZoomOut}
            >
              -
            </Button>
            <span className="text-white text-sm">
              {Math.round(scale * 100)}%
            </span>
            <Button
              color="primary"
              onClick={handleZoomIn}
            >
              +
            </Button>
            <Button
              color="primary"
              onClick={handleResetZoom}
            >
              Fit
            </Button>
          </div>

          <div className="max-h-[80vh] max-w-full overflow-auto">
            <canvas ref={canvasRef} />
          </div>

          <div className="flex items-center justify-center mt-6">
            <Button
              color="primary"
              onClick={previousPage}
              disabled={pageNum === 1}
            >
              <ChevronLeftIcon className="h-5 w-5 text-inherit" />
            </Button>
            <span className="mx-4 text-white font-semibold">
              {pageNum} / {pageCount}
            </span>
            <Button
              color="primary"
              onClick={nextPage}
              disabled={pageNum === pageCount}
            >
              <ChevronRightIcon className="h-5 w-5 text-inherit" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PDFViewer;
